import {
    PageTypeV4,
    SchemaType,
    ManifestSection,
    MANIFESTPATH,
    SchemaTag,
    DefinitionName,
    LogSeverity,
    PropertyName
} from '@sap/ux-specification-types';
import type { Manifest, v4, ImportListReportV4Parameters, SapUi5RoutingTarget } from '@sap/ux-specification-types';
import { DEFINITION_LINK_PREFIX, getPageTypeV4, removeEmptyStructure, getDefinitionKey } from '../../../common';
import { importSettingsOfObject } from '../utils';
import { initI18n } from '../../../../i18n/i18n';
import i18next from 'i18next';
import { log } from '../../../../extensionLogger';
import { MetadataInstanceFactoryV4 as Factory } from '../../export/factory';
import type { MetadataInstanceInterface } from '../../../common/decoration/factory';
import type { Definition, DefinitionOrBoolean } from 'typescript-json-schema';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { ImportActionSettingsOfReferenceParams } from '../../utils/utils';
import { importActionSettingsOfReference } from '../../utils/utils';

/**
 * Imports the content of annotation-based as well as custom columns.
 *
 * @param config - the configuration to be filled
 * @param importParameters  - object comprising all input data
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageType - page type
 * @param routingId - ID of the given page
 * @param configId - config id
 * @param viewId - view id in multi view mode
 */
function transferColumns(
    config: v4.Table | v4.LRTableView,
    importParameters: ImportListReportV4Parameters,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string,
    configId?: string,
    viewId?: string
) {
    const schemaDefinitions = importParameters.jsonSchema.definitions;
    const lineItemsId = configId && configId !== `@${UIAnnotationTerms.LineItem}` ? configId : DefinitionName.LineItems;
    const lineItems = schemaDefinitions[lineItemsId] as Definition;
    if (lineItems && Object.keys(lineItems.properties).length > 0) {
        config.columns = {};
        for (const columnKey in lineItems.properties) {
            const columnDefinition = (schemaDefinitions[lineItemsId] as Definition).properties[columnKey] as Definition;
            const schemaPropertyName = columnDefinition.$ref.split(DEFINITION_LINK_PREFIX)[1].split('<')[0];
            config.columns[columnKey] = factory.createInstance(pageType, schemaPropertyName);
            const targetAnnotation = viewId || configId;
            importSettingsOfObject(
                config.columns[columnKey],
                importParameters.manifest,
                schemaDefinitions[schemaPropertyName] as Definition,
                routingId,
                [columnKey, targetAnnotation],
                targetAnnotation,
                importParameters.logger
            );
            removeEmptyStructure(config.columns, columnKey);
        }
        removeEmptyStructure(config, 'columns');
    } else {
        const views = (importParameters.manifest['sap.ui5']?.routing?.targets?.[routingId] as SapUi5RoutingTarget)
            ?.options?.settings?.views;
        const viewsCount =
            views && typeof views === 'object' && views.paths?.length !== undefined ? views.paths.length : 0;
        if (viewsCount < 1) {
            log(importParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOLINEITEMS')
            });
        }
    }
}

/**
 * Import visual filters of list report.
 *
 * @param {ImportListReportV4Parameters} importParameters  - object comprising all input data
 * @param {v4.FilterBar} filterBarConfig - the configuration to be filled
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param {PageTypeV4} pageType - page type
 * @param {string} routingId - page key in manifest
 */
function importVisualFilters(
    importParameters: ImportListReportV4Parameters,
    filterBarConfig: v4.FilterBar,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string
) {
    const schemaDefinitions = importParameters.jsonSchema.definitions;
    const visualFilters = schemaDefinitions[DefinitionName.VisualFilters];
    filterBarConfig.visualFilters = {};
    if (typeof visualFilters === 'object') {
        for (const visualFilter in visualFilters.properties) {
            const filterDefinition = visualFilters.properties[visualFilter];
            if (typeof filterDefinition === 'object' && filterDefinition.$ref) {
                const definitionKey = getDefinitionKey(filterDefinition.$ref);
                const schemaPropertyName = definitionKey.split('<')[0];
                filterBarConfig.visualFilters[visualFilter] = factory.createInstance(pageType, schemaPropertyName);
                importSettingsOfObject(
                    filterBarConfig.visualFilters[visualFilter],
                    importParameters.manifest,
                    schemaDefinitions[definitionKey] as Definition,
                    routingId,
                    [visualFilter]
                );
            }
        }
        removeEmptyStructure(filterBarConfig, 'visualFilters');
    }
}

/**
 * Import table toolbar actions of list report.
 *
 * @param importParameters  - object comprising all input data
 * @param tableConfig - configuration object for the table or table view
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageType - page type
 * @param routingId - The unique identifier for the routing configuration in the manifest.
 * @param columnDefinitionName - The name of the column definition to be used.
 * @param viewId - The identifier for the specific view in multi-view mode.
 */
function importActions(
    importParameters: ImportListReportV4Parameters,
    tableConfig: v4.Table | v4.LRTableView | v4.ALPTableView,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string,
    columnDefinitionName: string = DefinitionName.LineItems,
    viewId?: string
) {
    const schemaDefinitions = importParameters.jsonSchema.definitions;
    let targetAnnotation = viewId;
    if (
        !targetAnnotation &&
        [DefinitionName.LineItems, `${DefinitionName.LineItems}OfSPV::LineItem`].indexOf(columnDefinitionName) === -1
    ) {
        targetAnnotation = `@${UIAnnotationTerms.LineItem}#${columnDefinitionName.split('::').pop()}`;
    }
    const actionsDefinition = schemaDefinitions[`${DefinitionName.Actions}<${columnDefinitionName}>`];
    if (typeof actionsDefinition === 'object') {
        for (const actionKey in actionsDefinition.properties) {
            const actionDefinition = actionsDefinition.properties[actionKey];
            if (typeof actionDefinition === 'object' && actionDefinition.$ref) {
                const definition = actionsDefinition?.['properties']?.[actionKey] as Definition;

                const importActionParams: ImportActionSettingsOfReferenceParams = {
                    actionPropertyDefinition: definition,
                    appSchema: importParameters.jsonSchema,
                    factory,
                    actionsInConfig: tableConfig.toolBar.actions,
                    actionKey,
                    manifest: importParameters.manifest,
                    routingId,
                    targetAnnotation
                };
                importActionSettingsOfReference(importActionParams);
            }
        }
    }
}

/**
 * Transfer toolbar settings of list report.
 *
 * @param importParameters - Object comprising all input data for the import process.
 * @param config - The configuration object to be filled with imported settings.
 * @param factory - Factory instance for generating metadata access objects.
 * @param pageType - The type of the page being processed (e.g., ListReport or AnalyticalListPage).
 * @param routingId - The unique identifier for the routing configuration in the manifest.
 * @param columnDefinitionName - The name of the column definition to be used.
 * @param viewId - The identifier for the specific view in multi-view mode, if applicable.
 */
function transferToolBar(
    importParameters: ImportListReportV4Parameters,
    config: v4.ListReportConfigV4 | v4.Table,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string,
    columnDefinitionName: string = DefinitionName.LineItems,
    viewId?: string
) {
    const toolbarInstance = factory.createInstance(pageType, DefinitionName.ToolBar, {
        actions: {}
    });
    let table;
    if (!viewId && config && 'table' in config) {
        table = config.table;
    } else {
        table = config;
    }
    table.toolBar = toolbarInstance;

    importSettingsOfObject(
        table.toolBar,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.ToolBarLR],
        routingId,
        []
    );
    importActions(importParameters, table, factory, pageType, routingId, columnDefinitionName, viewId);

    removeEmptyStructure(table.toolBar, 'actions');
    removeEmptyStructure(table, 'toolBar');
}

/**
 * Determines the target annotation of a list report view or defaultTemplateAnnotationPath.
 *
 * @param schemaDefinitions - app schema definitions
 * @param definitionName - current definition name
 * @param entitySet - The name of the entity set to be used for determining the target annotation
 * @returns the target annotation as string
 */
function determineTargetAnnotationOfView(
    schemaDefinitions: Definition,
    definitionName: string,
    entitySet?: string
): string {
    const lineItemAnnotation = schemaDefinitions[definitionName]?.[SchemaTag.annotationPath];
    const targetAnnotationPrefix = entitySet ? `/${entitySet}/` : '';
    let targetAnnotation = lineItemAnnotation?.substr(lineItemAnnotation.lastIndexOf('@'));
    targetAnnotation = targetAnnotationPrefix ? `${targetAnnotationPrefix}${targetAnnotation}` : targetAnnotation;
    return targetAnnotation;
}

/**
 * Import chart & table view of ALP.
 *
 * @param importParameters  - object comprising all input data
 * @param config - the configuration to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageType - page type
 * @param routingId - page key in manifest
 */
function importALPViews(
    importParameters: ImportListReportV4Parameters,
    config: v4.ListReportConfigV4,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string
): void {
    const schemaDefinitions = importParameters.jsonSchema.definitions;
    //chart view
    config.chart = factory.createInstance(pageType, DefinitionName.ALPChart);
    const chartDefinition = schemaDefinitions[DefinitionName.ALPChart] as Definition;
    const chartAnnotationPath = chartDefinition[SchemaTag.annotationPath];
    importSettingsOfObject(config.chart, importParameters.manifest, chartDefinition, routingId, [
        chartDefinition[SchemaTag.key],
        chartAnnotationPath?.substr(chartAnnotationPath.lastIndexOf('@'))
    ]);
    config.chart.toolBar = factory.createInstance(pageType, DefinitionName.ToolBar, { actions: {} });
    importSettingsOfObject(
        config.chart.toolBar,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.ViewChartToolBar] as Definition,
        routingId,
        []
    );
    //table view
    const tableAnnotationPath = schemaDefinitions[DefinitionName.ALPTable][SchemaTag.annotationPath];
    if (tableAnnotationPath) {
        const tableDefinition = schemaDefinitions[DefinitionName.ALPTable];
        const tableConfig = (config.table = factory.createInstance(
            pageType,
            DefinitionName.ALPTable
        ) as v4.ALPTableView);
        transferToolBar(importParameters, config, factory, pageType, routingId);
        const configId = tableAnnotationPath.substr(tableAnnotationPath.lastIndexOf('@'));
        importSettingsOfObject(tableConfig, importParameters.manifest, tableDefinition, routingId, [
            tableDefinition[SchemaTag.key],
            configId
        ]);
        //transfer columns
        transferColumns(tableConfig, importParameters, factory, pageType, routingId, undefined, configId);
    }
}

/**
 * Method received view definition name and returns view type.
 *
 * @param {string} definition  - definition name in schema.
 * @returns {DefinitionName | undefined} Resolved view type.
 */
function getViewTypeByDefinition(definition: string): DefinitionName | undefined {
    let viewType: DefinitionName;
    if (definition.indexOf(DefinitionName.LRTableView) > -1) {
        viewType = DefinitionName.LRTableView;
    } else if (definition.indexOf(DefinitionName.LRChartView) > -1) {
        viewType = DefinitionName.LRChartView;
    } else if (definition === DefinitionName.TableViewExtension) {
        viewType = DefinitionName.TableViewExtension;
    }
    return viewType;
}

/**
 * Import table views of list report.
 *
 * @param importParameters  - object comprising all input data
 * @param config - the configuration to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageType - page type
 * @param routingId - page key in manifest
 */
function importLRViews(
    importParameters: ImportListReportV4Parameters,
    config: v4.ListReportConfigV4,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string
): void {
    const tableViewConfig: v4.MultiTableModeV4 = ((config.table as v4.Table).views = factory.createInstance(
        pageType,
        DefinitionName.MultiTableModeV4
    ));
    const schemaDefinitions = importParameters.jsonSchema.definitions as Definition;

    //Transfer settings on overall views' level
    importSettingsOfObject(
        tableViewConfig,
        importParameters.manifest,
        schemaDefinitions[DefinitionName.MultiTableModeV4],
        routingId,
        []
    );
    const properties = schemaDefinitions[DefinitionName.MultiTableModeV4].properties;
    const sortedList = Object.entries(properties).sort(
        (a, b) => a[1][SchemaTag.propertyIndex] - b[1][SchemaTag.propertyIndex]
    );
    for (const element of sortedList) {
        //process single views
        if (element[1]['$ref']) {
            const nextDefinition = element[1]['$ref'].split(DEFINITION_LINK_PREFIX)[1];
            const viewType = getViewTypeByDefinition(nextDefinition);
            const tableView = (tableViewConfig[element[0]] = factory.createInstance(pageType, viewType));
            const entitySet = schemaDefinitions[nextDefinition]['entitySet'];
            const targetAnnotation = determineTargetAnnotationOfView(schemaDefinitions, nextDefinition, entitySet);
            importSettingsOfObject(tableView, importParameters.manifest, schemaDefinitions[nextDefinition], routingId, [
                element[0],
                targetAnnotation
            ]);
            if (viewType === DefinitionName.LRTableView) {
                const definitionName = `${DefinitionName.LineItemsOfView}::${element[0]}`;
                transferToolBar(
                    importParameters,
                    tableView,
                    factory,
                    pageType,
                    routingId,
                    definitionName,
                    targetAnnotation
                );
                transferColumns(
                    tableView,
                    importParameters,
                    factory,
                    pageType,
                    routingId,
                    definitionName,
                    targetAnnotation
                );
            }
            if (Object.keys(tableView).length === 0) {
                delete tableViewConfig[element[0]];
            }
        }
    }
    removeEmptyStructure(config.table, 'views');
}

/**
 * Fills the header actions in the config file of a V4 list report page.
 *
 * @param appSchema - app specific schema
 * @param listReportPageConfig - the configuration object for the list report page
 * @param factory - factory, for generating the access to reflect-metadata
 * @param manifest - manifest.json of the app
 * @param routingId - the unique identifier for the routing configuration in the manifest
 */
function transferHeaderActions(
    appSchema: Definition,
    listReportPageConfig: v4.ListReportConfigV4,
    factory: MetadataInstanceInterface,
    manifest: Manifest,
    routingId: string
): void {
    const definitionName = DefinitionName.HeaderActions;
    const actionType = PropertyName.header;
    const actions = appSchema.definitions[definitionName] as Definition;
    if (typeof actions === 'object') {
        listReportPageConfig[actionType].actions = {};
        for (const actionKey in actions.properties) {
            const definition = actions.properties[actionKey];
            if (typeof definition === 'object' && definition.$ref) {
                const definitionKey = getDefinitionKey(definition.$ref);
                const actionDefinition = appSchema.definitions[definitionKey] as Definition;
                const key = getDefinitionKey(actionDefinition.$ref || definition.$ref);

                listReportPageConfig[actionType].actions[actionKey] = factory.createInstance(
                    PageTypeV4.ObjectPage,
                    key
                );
                importSettingsOfObject(
                    listReportPageConfig[actionType].actions[actionKey],
                    manifest,
                    appSchema.definitions[key],
                    routingId,
                    [actionKey.replace('/', '??')]
                );
                removeEmptyStructure(listReportPageConfig.header, 'actions');
                removeEmptyStructure(listReportPageConfig, 'header');
            }
        }
    }
}

/**
 * Import custom filter fields of list report.
 *
 * @param {ImportListReportV4Parameters} importParameters  - object comprising all input data
 * @param {v4.FilterBar} filterBarConfig - the configuration to be filled
 * @param {MetadataInstanceInterface} factory - factory, for generating the access to reflect-metadata
 * @param {string} routingId - page key in manifest
 */
function importCustomFilterFields(
    importParameters: ImportListReportV4Parameters,
    filterBarConfig: v4.FilterBar,
    factory: MetadataInstanceInterface,
    routingId: string
) {
    const schemaDefinitions = importParameters.jsonSchema.definitions;
    let filterFieldsProperties: { [key: string]: DefinitionOrBoolean } = {};
    for (const definition of [DefinitionName.SelectionFields, DefinitionName.CompactFilters]) {
        const filterFieldsSchema = schemaDefinitions[definition];
        if (
            typeof filterFieldsSchema === 'object' &&
            filterFieldsSchema.properties &&
            Object.keys(filterFieldsSchema.properties).length
        ) {
            filterFieldsProperties = filterFieldsSchema.properties;
        }
    }
    for (const filterFieldId in filterFieldsProperties) {
        const schemaFilterField = filterFieldsProperties[filterFieldId];
        if (
            typeof schemaFilterField !== 'object' ||
            schemaFilterField.$ref !== DEFINITION_LINK_PREFIX + DefinitionName.CustomFilterField
        ) {
            continue;
        }
        // Handle custom filter field
        const customFilterField = factory.createInstance(PageTypeV4.ListReport, DefinitionName.CustomFilterField);
        const sectionIdRef = schemaFilterField['$ref'].split(DEFINITION_LINK_PREFIX)[1];
        importSettingsOfObject(
            customFilterField,
            importParameters.manifest,
            importParameters.jsonSchema.definitions[sectionIdRef],
            routingId,
            [filterFieldId]
        );
        if (!filterBarConfig.selectionFields) {
            filterBarConfig.selectionFields = {};
        }
        filterBarConfig.selectionFields[filterFieldId] = customFilterField;
    }
}

/**
 * Adds manifest settings to the config.json of LR or ALP V4.
 *
 * @param importParameters  - object comprising all input data
 * @param config - the configuration to be filled
 * @param factory - factory, for generating the access to reflect-metadata
 * @param pageType - page type
 * @param routingId - page key in manifest
 * @param schemaType - allows distinction of LR and ALP scenario
 */
export function addSettings(
    importParameters: ImportListReportV4Parameters,
    config: v4.ListReportConfigV4,
    factory: MetadataInstanceInterface,
    pageType: PageTypeV4,
    routingId: string,
    schemaType: SchemaType
): void {
    // Transfer page level settings with sync rules
    importSettingsOfObject(config, importParameters.manifest, importParameters.jsonSchema, routingId, []);

    config.header = factory.createInstance(pageType, DefinitionName.Header);
    importSettingsOfObject(
        config.header,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.Header],
        routingId,
        []
    );
    transferHeaderActions(importParameters.jsonSchema, config, factory, importParameters.manifest, routingId);

    //FilterBar:
    config.filterBar = factory.createInstance(pageType, DefinitionName.FilterBar);
    importSettingsOfObject(
        config.filterBar,
        importParameters.manifest,
        importParameters.jsonSchema.definitions[DefinitionName.FilterBar],
        routingId,
        []
    );
    importVisualFilters(importParameters, config.filterBar, factory, pageType, routingId);
    // Import custom filter fields
    importCustomFilterFields(importParameters, config.filterBar, factory, routingId);

    //Distinguish ALP table from LR table
    if (schemaType === SchemaType.ListReport) {
        let definitionName = DefinitionName.Table;
        let tableDefinition = importParameters.jsonSchema.definitions[definitionName] as Definition;
        let columnDefinitionName;
        if (tableDefinition.$ref) {
            //(S)PV scenario ==> delegation, go to TableSPV
            definitionName = tableDefinition.$ref.split(DEFINITION_LINK_PREFIX)[1] as DefinitionName;
            tableDefinition = importParameters.jsonSchema.definitions[definitionName] as Definition;
            columnDefinitionName = (tableDefinition.properties.columns as Definition).$ref.split(
                DEFINITION_LINK_PREFIX
            )[1];
        }
        config.table = factory.createInstance(pageType, DefinitionName.Table);
        const targetAnnotation = determineTargetAnnotationOfView(
            importParameters.jsonSchema.definitions,
            definitionName
        );
        importSettingsOfObject(config.table, importParameters.manifest, tableDefinition, routingId, [
            definitionName,
            targetAnnotation
        ]);
        //transfer toolbar
        transferToolBar(importParameters, config, factory, pageType, routingId, columnDefinitionName);
        //transfer columns
        transferColumns(
            config.table,
            importParameters,
            factory,
            pageType,
            routingId,
            columnDefinitionName,
            targetAnnotation
        );
        importLRViews(importParameters, config, factory, pageType, routingId);
    } else if (schemaType === SchemaType.AnalyticalListPage) {
        importALPViews(importParameters, config, factory, pageType, routingId);
    }
}

/**
 * Creates the configuration file content for a list report V4.
 *
 * @param importParameters  - object comprising all input data
 * @param schemaType - specifies whether the schema is for a List Report or Analytical List Page
 * @returns - the configuration (JSON) for the list report
 */
export function createListReportConfig(
    importParameters: ImportListReportV4Parameters,
    schemaType: SchemaType
): v4.ListReportConfigV4 | undefined {
    // Initialize i18next
    initI18n();
    if (!importParameters.jsonSchema) {
        return;
    }
    if (!importParameters.manifest[ManifestSection.ui5] || !importParameters.manifest[ManifestSection.ui5]['routing']) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOROUTING'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5]
            }
        });
        return;
    }
    const v4Pages = importParameters.manifest[ManifestSection.ui5]['routing']['targets'] as v4.SapUi5RoutingTargetsV4;
    if (!v4Pages) {
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOTARGETS'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5]
            }
        });
        return;
    }
    const factory = new Factory();
    //Instantiate Page
    const config = factory.createPageInstance(PageTypeV4.ListReport);

    for (const key in v4Pages) {
        const v4Page = v4Pages[key] as v4.SapUiAppPageV4;
        const pageType = getPageTypeV4(v4Page);
        const matchesId = v4Page.name && key === importParameters.routingId;
        const matchesSchema = [PageTypeV4.ListReport, PageTypeV4.AnalyticalListPage].includes(pageType);
        if (matchesId && matchesSchema) {
            addSettings(importParameters, config, factory, PageTypeV4.ListReport, key, schemaType);
            break;
        }
    }

    return config;
}
